import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useReports,
  useRunReport,
  useExportReport,
  useGenerateDailyStatement,
  useReportPrintOptions,
  useSaveReportPrintOptions,
  useDepartments,
  useDoctors,
  usePatients,
  useSearchPatients,
  useAssetCategories,
  useActiveServiceTypes,
  useActiveDiagnosticCategories,
} from '../../../services/httpServices/queries';
import { reportsService } from '../../../services/httpServices/reportsService';
import { joinFullName } from '../../../utils/patientName';
import type {
  ReportDefinition,
  ReportFilterKey,
  ReportPrintOptions,
  ReportResult,
  RunReportDto,
} from '../../../types/reports';
import type { Option } from '../../../components/ui/searchable-select';
import {
  monthBounds,
  yearBounds,
  derivePeriod,
  quickRanges,
  todayInTimeZone,
  toInputDate,
} from '../../../utils/reportPeriod';
import {
  DEFAULT_REPORT_PRINT_OPTIONS,
  hasPrintableSection,
} from '../../../utils/reportPrintOptions';
import { useDebounce } from '../../../hooks/useDebounce';
import { useHospitalTimezone } from '../../../hooks/useHospitalTimezone';
import { AlertCircle, FileText, Layers, LayoutList, Loader2 } from 'lucide-react';
import { describeError } from './reportFormat';
import { ReportLibrary } from './components/ReportLibrary';
import { ReportConfigPanel } from './components/ReportConfigPanel';
import { ReportResults } from './components/ReportResults';
import { PrintOptionsModal } from './components/PrintOptionsModal';
import { DailyStatementArchive } from './components/DailyStatementArchive';

export function Reports() {
  const { data, isLoading, error } = useReports();
  const reports = useMemo<ReportDefinition[]>(() => data?.data ?? [], [data]);

  // Reports are anchored to the hospital's calendar (Settings → General), not
  // the workstation's. A browser in another timezone must not seed the page
  // with the wrong day, month or year.
  const { timezone: hospitalTimezone, isReady: timezoneReady } = useHospitalTimezone();
  const today = useMemo(() => todayInTimeZone(hospitalTimezone), [hospitalTimezone]);
  const todayKey = useMemo(() => toInputDate(today), [today]);
  // Default reporting window is the current calendar month, so the Month and
  // Year dropdowns start on the current month / current year.
  const defaultRange = useMemo(
    () => monthBounds(today.getFullYear(), today.getMonth()),
    [today],
  );

  // The URL is the source of truth for the selected report and its filters, so
  // a reload (or a shared link) restores the exact same view.
  const [searchParams, setSearchParams] = useSearchParams();

  const setParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');
  /** Whether the print-options modal is open. */
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const debouncedPatientSearch = useDebounce(patientSearch, 350);
  const [dateError, setDateError] = useState<string | null>(null);
  // Set when a card in the Daily Statement archive is opened. The effect below
  // waits for the URL date to settle, then generates that day's report.
  const [pendingStatementDate, setPendingStatementDate] = useState<string | null>(
    null,
  );

  // Seed the default window into the URL on first load, and transparently map
  // the retired "expense-category-breakdown" report onto the merged expense
  // report (its `type` becomes the grouping dimension) so old links keep working.
  // Wait for the hospital timezone so "this month" is the hospital's month.
  const seededDefaults = useRef(false);
  useEffect(() => {
    if (seededDefaults.current || !timezoneReady) return;
    seededDefaults.current = true;

    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (next.get('report') === 'expense-category-breakdown') {
      next.set('report', 'expenses');
      if (!next.get('type')) next.set('type', 'category');
      changed = true;
    }
    if (!next.get('from')) {
      next.set('from', defaultRange.from);
      changed = true;
    }
    if (!next.get('to')) {
      next.set('to', defaultRange.to);
      changed = true;
    }
    if (changed) setSearchParams(next, { replace: true });
    // `searchParams`/`setSearchParams` are intentionally omitted: the effect
    // must run once after the timezone arrives, not on every URL edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezoneReady, defaultRange.from, defaultRange.to]);

  const selectedId = searchParams.get('report');
  // An empty (or absent) `from`/`to` means "no date filter" — the default month
  // is only seeded into the URL on first load, so a cleared range stays cleared
  // instead of falling back to the current month.
  const dateFrom = searchParams.get('from') ?? '';
  const dateTo = searchParams.get('to') ?? '';
  const departmentId = searchParams.get('department') || '';
  const doctorId = searchParams.get('doctor') || '';
  const patientId = searchParams.get('patient') || '';
  const categoryId = searchParams.get('category') || '';
  // Service Type filter (the IPD Revenue Summary only). `type` is already the
  // grouping dimension, so this dedicated key avoids the collision.
  const serviceType = searchParams.get('serviceType') || '';
  // Diagnostic category filter (the OPD Revenue Summary only).
  const diagnosticCategory = searchParams.get('diagnosticCategory') || '';
  // Which date the period filters on. Only the two financial revenue reports
  // declare support; everywhere else it stays hidden and ignored.
  const dateBasis: 'document' | 'settled' =
    searchParams.get('basis') === 'settled' ? 'settled' : 'document';

  const selectedReport = useMemo(
    () => reports.find((report) => report.id === selectedId) ?? null,
    [reports, selectedId],
  );

  // What the PDF / print copy contains is a shared, server-saved layout per
  // report (section switches, hidden items and custom labels). The report on
  // screen is always the canonical, complete result.
  const printOptionsQuery = useReportPrintOptions(selectedId ?? null);
  const savePrintOptions = useSaveReportPrintOptions();
  const savedPrintOptions: ReportPrintOptions =
    printOptionsQuery.data?.data ?? DEFAULT_REPORT_PRINT_OPTIONS;
  const hasPrintSection = hasPrintableSection(
    savedPrintOptions,
    selectedReport?.printWithoutDetails,
  );
  const handleSavePrintOptions = (options: ReportPrintOptions) => {
    if (!selectedReport) return;
    savePrintOptions.mutate(
      { id: selectedReport.id, options },
      { onSuccess: () => setPrintOptionsOpen(false) },
    );
  };

  const groupBy = searchParams.get('type') || selectedReport?.groupByOptions?.[0]?.value || '';
  const supports = (filter: ReportFilterKey) =>
    selectedReport?.supportedFilters?.includes(filter) ?? false;
  // Grouping by category (with or without the date column) makes a category
  // filter redundant, so the filter is disabled for both category modes.
  const isCategoryGroup = groupBy === 'category' || groupBy === 'category-no-date';

  const period = derivePeriod(dateFrom, dateTo);

  // Mirrors the backend's single-day narrowing: a daily statement always
  // covers one day, never a future one.
  const statementDate = useMemo(() => {
    const fromKey = dateFrom.slice(0, 10);
    const toKey = dateTo.slice(0, 10);
    let date = toKey || fromKey || todayKey;
    if (fromKey && toKey && fromKey !== toKey) date = toKey > todayKey ? todayKey : toKey;
    if (date > todayKey) date = todayKey;
    if (fromKey && fromKey > date) date = todayKey;
    return date;
  }, [dateFrom, dateTo, todayKey]);

  const ranges = useMemo(() => quickRanges(today), [today]);

  const applyRange = (from: string, to: string) => {
    setParams({ from, to });
    setDateError(null);
  };

  const yearOptions = useMemo(() => {
    const current = today.getFullYear();
    const years: number[] = [];
    for (let year = current; year >= current - 6; year -= 1) years.push(year);
    // Keep a manually-selected older year visible even if it falls outside the default window.
    if (period.year < current - 6) years.push(period.year);
    return years;
  }, [today, period.year]);

  const departmentsQuery = useDepartments();
  const doctorsQuery = useDoctors(1, 1000);
  const patientsQuery = usePatients(1, 1000);
  // Reports are historical, so inactive patients must remain selectable.
  const patientSearchQuery = useSearchPatients(debouncedPatientSearch, true);
  const expenseCategoriesQuery = useAssetCategories(undefined, true, 'EXPENSE');
  // The IPD Revenue Summary's type filter is the managed Service Types catalogue.
  const serviceTypesQuery = useActiveServiceTypes();
  // The OPD Revenue Summary's category filter is the Diagnostics → Categories
  // catalogue.
  const diagnosticCategoriesQuery = useActiveDiagnosticCategories();

  const departmentOptions = useMemo<Option[]>(
    () =>
      (departmentsQuery.data?.data ?? [])
        .filter((department) => department.status !== 'INACTIVE')
        .map((department) => ({
          value: department.id,
          label: department.name,
          description: department.code,
        })),
    [departmentsQuery.data],
  );

  const categoryOptions = useMemo<Option[]>(
    () =>
      (expenseCategoriesQuery.data?.data ?? []).map((category) => ({
        value: category.id,
        label: category.name,
        description: category.code,
      })),
    [expenseCategoriesQuery.data],
  );

  const doctorOptions = useMemo<Option[]>(
    () =>
      (doctorsQuery.data?.data ?? []).map((doctor: any) => ({
        value: doctor.id,
        label: doctor.name,
        description: doctor.department?.name || doctor.employeeCode,
      })),
    [doctorsQuery.data],
  );

  const serviceTypeOptions = useMemo<Option[]>(() => {
    const options = (serviceTypesQuery.data?.data ?? []).map((type: any) => ({
      value: type.code,
      label: type.name,
      description: type.code,
    }));
    // Lines whose service was deleted/never catalogued are still reportable;
    // keep the bucket visible and selectable instead of hiding its money.
    if (!options.some((option) => option.value === 'unclassified')) {
      options.push({
        value: 'unclassified',
        label: 'Unclassified',
        description: 'Lines without a Service Type',
      });
    }
    return options;
  }, [serviceTypesQuery.data]);

  const diagnosticCategoryOptions = useMemo<Option[]>(() => {
    const options = (diagnosticCategoriesQuery.data?.data ?? []).map((category) => ({
      value: category.code,
      label: category.name,
      description: category.code,
    }));
    // Receipt lines that are not linked to a test still carry money: keep their
    // buckets visible and selectable instead of hiding them.
    if (!options.some((option) => option.value === 'accessory')) {
      options.push({
        value: 'accessory',
        label: 'Accessories',
        description: 'Accessory items on diagnostic receipts',
      });
    }
    if (!options.some((option) => option.value === 'uncategorized')) {
      options.push({
        value: 'uncategorized',
        label: 'Uncategorized',
        description: 'Tests without a category',
      });
    }
    return options;
  }, [diagnosticCategoriesQuery.data]);

  const patientOptions = useMemo<Option[]>(() => {
    const source = patientSearch.trim().length >= 2 ? patientSearchQuery.data?.data : patientsQuery.data?.data;
    return (source ?? []).map((patient: any) => ({
      value: patient.id,
      label: joinFullName(patient) || patient.registrationNumber,
      description: [patient.registrationNumber, patient.mobileNumber].filter(Boolean).join(' · '),
    }));
  }, [patientSearch, patientSearchQuery.data, patientsQuery.data]);

  const patientOptionsLoading =
    patientSearch.trim().length >= 2 ? patientSearchQuery.isLoading : patientsQuery.isLoading;

  const reportCategories = useMemo(
    () => Array.from(new Set(reports.map((report) => report.category))).sort(),
    [reports],
  );

  // The library's category chip lives in the URL too (`reportCategory`), so a
  // reload keeps the selected category. `category` is already taken by the
  // expense-category filter, hence the dedicated key.
  const reportCategoryParam = searchParams.get('reportCategory') || 'all';
  const categoryFilter =
    reportCategoryParam === 'all' ||
    reportCategories.length === 0 ||
    reportCategories.includes(reportCategoryParam)
      ? reportCategoryParam
      : 'all';

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    return reports.filter((report) => {
      if (categoryFilter !== 'all' && report.category !== categoryFilter) return false;
      if (!query) return true;
      return (
        report.name.toLowerCase().includes(query) ||
        report.description.toLowerCase().includes(query) ||
        report.category.toLowerCase().includes(query)
      );
    });
  }, [reports, search, categoryFilter]);

  const runReport = useRunReport();
  const exportReport = useExportReport();
  const generateStatement = useGenerateDailyStatement();
  // The printable copy is fetched directly (not cached) because it must always
  // reflect the filters currently applied to the form.
  const [isPrinting, setIsPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  const buildDto = (): RunReportDto => {
    const dto: RunReportDto = {};
    if (supports('date')) {
      if (dateFrom) dto.dateFrom = dateFrom;
      if (dateTo) dto.dateTo = dateTo;
      if (supports('dateBasis') && dateBasis === 'settled') dto.dateBasis = 'settled';
    }
    if (supports('department') && departmentId) dto.departmentId = departmentId;
    if (supports('doctor') && doctorId) dto.doctorId = doctorId;
    if (supports('patient') && patientId) dto.patientId = patientId;
    if (supports('category') && categoryId && !isCategoryGroup) dto.categoryId = categoryId;
    if (supports('serviceType') && serviceType) dto.serviceType = serviceType;
    if (supports('diagnosticCategory') && diagnosticCategory) {
      dto.diagnosticCategory = diagnosticCategory;
    }
    if (groupBy) dto.groupBy = groupBy;
    return dto;
  };

  const sameFilters = (a: RunReportDto, b: RunReportDto) =>
    (a.dateFrom ?? '') === (b.dateFrom ?? '') &&
    (a.dateTo ?? '') === (b.dateTo ?? '') &&
    (a.dateBasis ?? 'document') === (b.dateBasis ?? 'document') &&
    (a.departmentId ?? '') === (b.departmentId ?? '') &&
    (a.doctorId ?? '') === (b.doctorId ?? '') &&
    (a.patientId ?? '') === (b.patientId ?? '') &&
    (a.categoryId ?? '') === (b.categoryId ?? '') &&
    (a.serviceType ?? '') === (b.serviceType ?? '') &&
    (a.diagnosticCategory ?? '') === (b.diagnosticCategory ?? '') &&
    (a.groupBy ?? '') === (b.groupBy ?? '');

  const requestedDto = runReport.variables?.dto;
  // Only surface a result generated for the report + filters currently in the
  // form. This closes the race where a late response from a previous request
  // (or a previously selected report) would render data that no longer matches.
  const result: ReportResult | undefined =
    runReport.data?.data &&
    selectedReport &&
    runReport.data.data.reportId === selectedReport.id &&
    requestedDto &&
    sameFilters(requestedDto, buildDto())
      ? runReport.data.data
      : undefined;

  // Keep the rendered result and the exported file consistent: whenever a filter
  // changes, discard the previous result so the user must regenerate with the new filters.
  useEffect(() => {
    runReport.reset();
    exportReport.reset();
    generateStatement.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, dateBasis, departmentId, doctorId, patientId, categoryId, serviceType, diagnosticCategory, groupBy, selectedId]);

  // Opening an archived day updates the date filters first; once the URL has
  // settled, load that day's frozen statement. This runs after the reset effect
  // above, so the generated result is not immediately cleared.
  useEffect(() => {
    if (!pendingStatementDate) return;
    if (dateFrom !== pendingStatementDate || dateTo !== pendingStatementDate) return;
    const date = pendingStatementDate;
    setPendingStatementDate(null);
    runReport.mutate({ id: 'daily-statement', dto: { dateFrom: date, dateTo: date } });
  }, [pendingStatementDate, dateFrom, dateTo, runReport]);

  /** Applies a month (or whole year) to the From/To inputs. */
  const applyPeriod = (month: number | 'all', year: number) => {
    const range = month === 'all' ? yearBounds(year) : monthBounds(year, month);
    applyRange(range.from, range.to);
  };

  const resetFilters = () => {
    // Clear every filter — including the date range — rather than snapping back
    // to the default month, so "Reset" means "no filters applied".
    setParams({
      from: '',
      to: '',
      basis: '',
      department: '',
      doctor: '',
      patient: '',
      category: '',
      serviceType: '',
      diagnosticCategory: '',
      type: selectedReport?.groupByOptions?.[0]?.value ?? '',
    });
    setPatientSearch('');
    setDateError(null);
  };

  const selectReport = (report: ReportDefinition) => {
    if (report.id === selectedId) return;
    setParams({
      report: report.id,
      basis: '',
      department: '',
      doctor: '',
      patient: '',
      category: '',
      serviceType: '',
      diagnosticCategory: '',
      type: report.groupByOptions?.[0]?.value ?? '',
    });
    setPatientSearch('');
    setDateError(null);
    setPrintOptionsOpen(false);
    runReport.reset();
  };

  const handleRun = () => {
    if (!selectedReport) return;
    if (supports('date') && dateFrom && dateTo && dateFrom > dateTo) {
      setDateError('"Date From" must be on or before "Date To".');
      return;
    }
    setDateError(null);
    exportReport.reset();
    runReport.mutate({ id: selectedReport.id, dto: buildDto() });
  };

  /**
   * Opens the print-options picker.
   *
   * The picker lists the report's own summary cards and breakdown rows, so a
   * result must exist even when the user has not clicked "Generate report" yet:
   * run it silently with the filters on screen. `PrintOptionsModal` seeds its
   * draft again as soon as that result arrives.
   */
  const openPrintOptions = () => {
    if (!selectedReport) return;
    setPrintOptionsOpen(true);
    if (!result && !runReport.isPending) handleRun();
  };

  const openDailyStatement = (date: string) => {
    if (selectedReport?.id !== 'daily-statement') return;
    setDateError(null);
    setPendingStatementDate(date);
    setParams({ from: date, to: date });
  };

  const handleSaveStatement = () => {
    if (!selectedReport || selectedReport.id !== 'daily-statement') return;
    if (statementDate > todayKey) {
      setDateError('A daily statement cannot be generated for a future date.');
      return;
    }
    setDateError(null);
    generateStatement.mutate(
      { date: statementDate, regenerate: true },
      {
        onSuccess: () => {
          runReport.reset();
          runReport.mutate({ id: selectedReport.id, dto: buildDto() });
        },
      },
    );
  };

  const handleExport = (format: 'pdf' | 'excel' | 'csv') => {
    if (!selectedReport) return;
    if (supports('date') && dateFrom && dateTo && dateFrom > dateTo) {
      setDateError('"Date From" must be on or before "Date To".');
      return;
    }
    // A PDF with no section ticked would be a bare letterhead: refuse instead.
    if (format === 'pdf' && !hasPrintSection) return;
    exportReport.mutate({
      id: selectedReport.id,
      format,
      filters: buildDto(),
    });
  };

  /**
   * Opens the printable A4 copy of the report (letterhead plus the saved print
   * layout) in a new window and lets the document open the browser's print
   * dialog.
   */
  const handlePrint = async () => {
    if (!selectedReport) return;
    if (supports('date') && dateFrom && dateTo && dateFrom > dateTo) {
      setDateError('"Date From" must be on or before "Date To".');
      return;
    }
    if (!hasPrintSection) {
      setPrintError('Select at least one section (Details, Summary or Breakdown) to print.');
      return;
    }
    // Opened synchronously so the popup blocker sees a user gesture.
    const win = window.open('', '_blank');
    if (!win) {
      setPrintError('Please allow pop-ups for this site to open the printable report.');
      return;
    }
    setIsPrinting(true);
    setPrintError(null);
    try {
      win.document.write(
        '<p style="font-family:system-ui,sans-serif;padding:24px;color:#475569">Preparing the printable report…</p>',
      );
      const html = await reportsService.printReport(selectedReport.id, buildDto());
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (error) {
      win.close();
      setPrintError(describeError(error, 'Failed to prepare the printable report.'));
    } finally {
      setIsPrinting(false);
    }
  };

  const runError = runReport.error as any;
  const exportError = exportReport.error as any;

  const activeFilterCount =
    (supports('date') && (dateFrom || dateTo) ? 1 : 0) +
    (supports('dateBasis') && dateBasis === 'settled' ? 1 : 0) +
    (supports('department') && departmentId ? 1 : 0) +
    (supports('doctor') && doctorId ? 1 : 0) +
    (supports('patient') && patientId ? 1 : 0) +
    (supports('category') && categoryId && !isCategoryGroup ? 1 : 0) +
    (supports('serviceType') && serviceType ? 1 : 0) +
    (supports('diagnosticCategory') && diagnosticCategory ? 1 : 0);

  return (
    <div className="flex flex-col gap-6 xl:h-full xl:min-h-0 xl:overflow-hidden">
      <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
            <FileText size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-text">Reports</h1>
            <p className="text-sm text-text-muted">
              Generate operational, clinical and financial reports, then print them or export them as PDF, Excel or CSV.
            </p>
          </div>
        </div>
        <div className="hidden items-center gap-2 sm:flex">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted shadow-sm">
            <LayoutList size={13} />
            {reports.length} reports
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted shadow-sm">
            <Layers size={13} />
            {reportCategories.length} categories
          </span>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger shrink-0">
          <AlertCircle size={18} />
          <span>Failed to load the list of available reports.</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-6 xl:flex-1 xl:min-h-0">
          <ReportLibrary
            filteredReports={filteredReports}
            reportCategories={reportCategories}
            search={search}
            onSearchChange={setSearch}
            categoryFilter={categoryFilter}
            onCategoryFilterChange={(value) =>
              setParams({ reportCategory: value === 'all' ? '' : value })
            }
            selectedId={selectedId}
            onSelect={selectReport}
          />

          <section className="space-y-6 min-w-0 xl:min-h-0 xl:overflow-y-auto xl:pr-1">
            {!selectedReport ? (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface/60 px-6 py-20 text-center text-text-muted">
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/15">
                  <FileText size={26} />
                </div>
                <p className="text-sm font-medium text-text">No report selected</p>
                <p className="mt-1 max-w-sm text-xs">
                  Pick a report from the list to configure its filters and generate the results.
                </p>
              </div>
            ) : (
              <>
                <ReportConfigPanel
                  selectedReport={selectedReport}
                  groupBy={groupBy}
                  isCategoryGroup={isCategoryGroup}
                  supports={supports}
                  activeFilterCount={activeFilterCount}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  dateBasis={dateBasis}
                  onDateBasisChange={(value) => {
                    setParams({ basis: value === 'settled' ? 'settled' : '' });
                    setDateError(null);
                  }}
                  timezone={hospitalTimezone}
                  period={period}
                  ranges={ranges}
                  yearOptions={yearOptions}
                  dateError={dateError}
                  statementDate={statementDate}
                  onApplyRange={applyRange}
                  onPeriodChange={applyPeriod}
                  onDateFromChange={(value) => {
                    setParams({ from: value });
                    setDateError(null);
                  }}
                  onDateToChange={(value) => {
                    setParams({ to: value });
                    setDateError(null);
                  }}
                  onGroupByChange={(value) => {
                    // Grouping by category makes a category filter pointless, so
                    // clear it rather than leave a stale selection hidden behind
                    // the disabled dropdown.
                    setParams(
                      value === 'category' || value === 'category-no-date'
                        ? { type: value, category: '' }
                        : { type: value },
                    );
                  }}
                  onReset={resetFilters}
                  onRun={handleRun}
                  running={runReport.isPending}
                  onSaveStatement={handleSaveStatement}
                  savingStatement={generateStatement.isPending}
                  onExport={handleExport}
                  exporting={exportReport.isPending}
                  exportFormat={exportReport.variables?.format}
                  printOptions={savedPrintOptions}
                  onOpenPrintOptions={openPrintOptions}
                  hasPrintSection={hasPrintSection}
                  onPrint={handlePrint}
                  printing={isPrinting}
                  canExport={Boolean(result)}
                  categoryId={categoryId}
                  categoryOptions={categoryOptions}
                  categoryLoading={expenseCategoriesQuery.isLoading}
                  onCategoryChange={(value) => setParams({ category: value })}
                  serviceType={serviceType}
                  serviceTypeOptions={serviceTypeOptions}
                  serviceTypeLoading={serviceTypesQuery.isLoading}
                  onServiceTypeChange={(value) => setParams({ serviceType: value })}
                  diagnosticCategory={diagnosticCategory}
                  diagnosticCategoryOptions={diagnosticCategoryOptions}
                  diagnosticCategoryLoading={diagnosticCategoriesQuery.isLoading}
                  onDiagnosticCategoryChange={(value) =>
                    setParams({ diagnosticCategory: value })
                  }
                  departmentId={departmentId}
                  departmentOptions={departmentOptions}
                  departmentLoading={departmentsQuery.isLoading}
                  onDepartmentChange={(value) => setParams({ department: value })}
                  doctorId={doctorId}
                  doctorOptions={doctorOptions}
                  doctorLoading={doctorsQuery.isLoading}
                  onDoctorChange={(value) => setParams({ doctor: value })}
                  patientId={patientId}
                  patientOptions={patientOptions}
                  patientLoading={patientOptionsLoading}
                  onPatientChange={(value) => {
                    setParams({ patient: value });
                    if (!value) setPatientSearch('');
                  }}
                  onPatientSearchChange={setPatientSearch}
                />

                {selectedReport.id === 'daily-statement' && (
                  <DailyStatementArchive
                    selectedDate={statementDate}
                    onOpen={openDailyStatement}
                  />
                )}

                {runError && !runReport.isPending && (
                  <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
                    <AlertCircle size={18} />
                    <span>{describeError(runError, 'Failed to generate the report.')}</span>
                  </div>
                )}

                {generateStatement.error && !generateStatement.isPending && (
                  <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
                    <AlertCircle size={18} />
                    <span>{describeError(generateStatement.error, 'Failed to save the daily statement.')}</span>
                  </div>
                )}

                {exportError && !exportReport.isPending && (
                  <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
                    <AlertCircle size={18} />
                    <span>{describeError(exportError, 'Failed to export the report.')}</span>
                  </div>
                )}

                {printError && (
                  <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
                    <AlertCircle size={18} />
                    <span>{printError}</span>
                  </div>
                )}

                {result && <ReportResults result={result} groupBy={groupBy} />}

                <PrintOptionsModal
                  open={printOptionsOpen}
                  report={selectedReport}
                  result={result}
                  saved={savedPrintOptions}
                  loading={printOptionsQuery.isLoading || (runReport.isPending && !result)}
                  saving={savePrintOptions.isPending}
                  onSave={handleSavePrintOptions}
                  onClose={() => setPrintOptionsOpen(false)}
                />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
